const { CHANNEL_PRICING } = require("../common/constants/pricing");

/**
 * Represents a marketing channel.
 * @typedef {Object} Channel
 * @property {string} channel_short_name - The short name of the channel.
 * @property {string} channel_full_name - The full name of the channel.
 * @property {string} description - A brief description of the channel.
 * @property {number} price - The pricing for the channel.
 * @property {number} minCampaignDuration - Minimum campaign duration in days.
 * @property {string} priority - Priority level of the channel.
 * @property {boolean} switchedOn - Indicates if the channel is active.
 * @property {Array} examples - Example ads for the channel.
 * @property {number} category - Category identifier for the channel.
 * @property {number} minAspect - Minimum aspect ratio.
 * @property {number} maxAspect - Maximum aspect ratio.
 * @property {number} defaultAspect - Default aspect ratio.
 * @property {Object} limits - Limits on various text fields.
 * @property {boolean} allowVideos - Indicates if videos are allowed.
 * @property {boolean} isSponsored - Indicates if the channel is for sponsored content.
 * @property {boolean} [isActive=true] - Indicates if the channel is currently active and will be shown up on the budget check.
 * @property {boolean} [shouldShowInSelectedChannels=true] - Indicates if the channel should be shown in the selected channels list.
 */

/**
 * @type {Channel[]}
 */
const channels = [
  {
    channel_short_name: "facebook",
    channel_full_name: "Facebook",
    description: "Sponsored post shown in Facebook feed",
    price: CHANNEL_PRICING.facebook,
    minCampaignDuration: 14,
    priority: "high",
    switchedOn: true,
    examples: [],
    category: 1,
    minAspect: 1,
    maxAspect: 1.91,
    defaultAspect: 1,
    limits: {
      displayLink: 100,
      title: 120,
      subHeadline: 100
    },
    allowVideos: true,
    isSponsored: true,
    isActive: true,
    shouldShowInSelectedChannels: true
  },
  {
    channel_short_name: "instagram",
    channel_full_name: "Instagram",
    description: "Sponsored ad on Instagram feed or Instagram Stories.",
    price: CHANNEL_PRICING.instagram,
    minCampaignDuration: 14,
    priority: "high",
    switchedOn: true,
    examples: [],
    category: 1,
    minAspect: 1,
    maxAspect: 1.91,
    defaultAspect: 1,
    limits: { displayLink: 100, title: 120, subHeadline: 100 },
    allowVideos: true,
    isSponsored: true,
    isActive: true,
    shouldShowInSelectedChannels: true
  },
  {
    channel_short_name: "google-display",
    channel_full_name: "Google Display Banner",
    description: "Display banners on pages relevant to the target group.",
    price: CHANNEL_PRICING["google-display"],
    minCampaignDuration: 14,
    priority: "high",
    switchedOn: true,
    examples: [],
    category: 2,
    minAspect: 1,
    maxAspect: 1.91,
    defaultAspect: 1.91,
    minHeight: 314,
    minWidth: 600,
    limits: {
      title: 30,
      companyName: 25,
      teaserText: 88
    },
    allowVideos: true,
    isSponsored: true,
    isActive: true,
    shouldShowInSelectedChannels: true
  },
  {
    channel_short_name: "youtube",
    channel_full_name: "YouTube Banner",
    description: "YouTube display banner of your job ad. Placement below relevant videos.",
    price: CHANNEL_PRICING.youtube,
    minCampaignDuration: 14,
    priority: "high",
    switchedOn: true,
    examples: [],
    category: 2,
    minAspect: 1,
    maxAspect: 1.91,
    defaultAspect: 1.91,
    minHeight: 314,
    minWidth: 600,
    limits: {
      title: 30,
      companyName: 25,
      teaserText: 88
    },
    allowVideos: true,
    isSponsored: true,
    isActive: true,
    shouldShowInSelectedChannels: true
  },
  {
    channel_short_name: "google-search",
    channel_full_name: "Google Search",
    description:
      "Google text ads. Shown above and below Google search results on the search network.",
    price: CHANNEL_PRICING["google-search"],
    minCampaignDuration: 14,
    priority: "secondary",
    switchedOn: true,
    examples: [],
    limits: {
      title: 30,
      teaserText: 88
    },
    isSponsored: true,
    isActive: true,
    shouldShowInSelectedChannels: true
  },
  {
    channel_short_name: "linkedin",
    channel_full_name: "LinkedIn Anzeige",
    description: "LinkedIn job posting.",
    price: CHANNEL_PRICING.linkedin,
    minCampaignDuration: 14,
    priority: "secondary",
    switchedOn: true,
    examples: [],
    category: 5,
    limits: {
      title: 120,
      companyName: 120,
      pageName: 120,
      companyId: 20
    },
    isSponsored: false,
    isActive: true,
    shouldShowInSelectedChannels: false
  },
  {
    channel_short_name: "snapchat",
    channel_full_name: "Snapchat",
    description: "Sponsored Ad.",
    price: CHANNEL_PRICING.snapchat,
    minCampaignDuration: 30,
    priority: "secondary",
    switchedOn: true,
    examples: [],
    minAspect: 0.5625, // 1:1.78
    maxAspect: 0.5625, // 1:1.78
    minHeight: 1920,
    minWidth: 1080,
    limits: {
      companyName: 25,
      title: 34,
      teaserText: 30
    },
    allowVideos: true,
    isSponsored: true,
    category: 6,
    isActive: true,
    shouldShowInSelectedChannels: true
  },
  {
    channel_short_name: "reddit",
    channel_full_name: "Reddit",
    description: "Sponsored Ad.",
    price: CHANNEL_PRICING.reddit,
    minCampaignDuration: 30,
    priority: "secondary",
    switchedOn: true,
    examples: [],
    minAspect: 1,
    maxAspect: 1.91,
    limits: {
      displayLink: 100,
      teaserText: 300
    },
    allowVideos: true,
    isSponsored: true,
    isActive: true,
    shouldShowInSelectedChannels: true
  },
  {
    channel_short_name: "ebay-kleinanzeigen",
    channel_full_name: "Kleinanzeigen",
    description: "Sponsored Ad.",
    price: CHANNEL_PRICING["ebay-kleinanzeigen"],
    minCampaignDuration: 14,
    priority: "secondary",
    switchedOn: true,
    examples: [],
    category: 2,
    minAspect: 1,
    maxAspect: 1.91,
    defaultAspect: 1.91,
    minHeight: 314,
    minWidth: 600,
    limits: {
      title: 30,
      companyName: 25,
      teaserText: 88
    },
    allowVideos: true,
    isSponsored: true,
    isActive: true,
    shouldShowInSelectedChannels: true
  },
  {
    channel_short_name: "indeed",
    channel_full_name: "Indeed Anzeige",
    description: "Dynamic Indeed CPC display.",
    price: CHANNEL_PRICING.indeed,
    minCampaignDuration: 14,
    priority: "secondary",
    switchedOn: true,
    examples: [],
    category: 5,
    limits: {
      title: 120,
      companyName: 120,
      pageName: 120
    },
    isSponsored: false,
    isActive: true,
    shouldShowInSelectedChannels: true
  },
  {
    channel_short_name: "twitter",
    channel_full_name: "X (ehemals Twitter)",
    description: "Sponsored Tweet.",
    price: CHANNEL_PRICING.twitter,
    minCampaignDuration: 14,
    priority: "secondary",
    switchedOn: true,
    examples: [],
    minAspect: 1,
    maxAspect: 1.91,
    minHeight: 800,
    minWidth: 800,
    limits: {
      teaserText: 255,
      title: 70,
      subHeadline: 200,
      displayLink: 100
    },
    allowVideos: true,
    isSponsored: true,
    category: 3,
    isActive: true,
    shouldShowInSelectedChannels: true
  },
  {
    channel_short_name: "pinterest",
    channel_full_name: "Pinterest Boost",
    description: "Sponsored Pinterest pin.",
    price: CHANNEL_PRICING.pinterest,
    minCampaignDuration: 30,
    priority: "secondary",
    switchedOn: true,
    examples: [],
    minAspect: 0.666666666, // 2:3
    maxAspect: 0.666666666, // 2:3
    limits: {
      title: 40,
      teaserText: 300
    },
    allowVideos: true,
    isSponsored: true,
    category: 7,
    isActive: true,
    shouldShowInSelectedChannels: true
  },
  {
    channel_short_name: "spotify",
    channel_full_name: "Spotify Jobanzeige",
    description: "Sponsored Spotify audio clip.",
    price: CHANNEL_PRICING.spotify,
    minCampaignDuration: 30,
    priority: "secondary",
    switchedOn: true,
    examples: [],
    minAspect: 1,
    maxAspect: 1,
    limits: {
      companyName: 25,
      title: 40,
      teaserText: 500
    },
    isSponsored: true,
    category: 8,
    isActive: true,
    shouldShowInSelectedChannels: true
  },
  {
    channel_short_name: "linkedin-feed",
    channel_full_name: "LinkedIn Feed-Anzeige",
    description: "LinkedIn Feed Ad.",
    price: CHANNEL_PRICING["linkedin-feed"],
    minCampaignDuration: 30,
    priority: "secondary",
    switchedOn: true,
    examples: [],
    isSponsored: true,
    category: 1,
    minAspect: 1,
    maxAspect: 1.91,
    defaultAspect: 1,
    limits: {
      displayLink: 100,
      title: 120,
      subHeadline: 100,
      teaserText: 600
    },
    allowVideos: true,
    isActive: true,
    shouldShowInSelectedChannels: true
  },
  {
    channel_short_name: "tiktok",
    channel_full_name: "TikTok",
    description: "TikTok ad.",
    price: CHANNEL_PRICING.tiktok,
    minCampaignDuration: 30,
    priority: "secondary",
    switchedOn: true,
    examples: [],
    minAspect: 0.5625, // 9:16
    maxAspect: 0.5625, // 1:1
    limits: {
      companyName: 20,
      teaserText: 100
    },
    allowVideos: true,
    isSponsored: true,
    category: 4,
    isActive: true,
    shouldShowInSelectedChannels: true
  }
];

/**
 * A mapping of channels by their short name.
 * @type {Object<string, Channel>}
 */
const channelsByShortName = channels.reduce(
  (acc, channel) => ({ ...acc, [channel.channel_short_name]: channel }),
  {}
);

/**
 * A mapping of channels by their full name.
 * @type {Object<string, Channel>}
 */
const channelsByFullName = channels.reduce(
  (acc, channel) => ({ ...acc, [channel.channel_full_name]: channel }),
  {}
);

module.exports = {
  channelsByShortName,
  channelsByFullName,
  channels
};
